import { Component, computed, inject, OnInit } from '@angular/core';
import { RestaurantStatusService } from '../../services/restaurant-status.service';
import { PublicScheduleStore } from '../../store/public-schedule.store';
import { CartConfigStore } from '../../store/cart-config.store';

type BannerMessage = { type: 'cart-disabled' | 'closed'; text: string } | null;

@Component({
  selector: 'app-top-navbar',
  templateUrl: './top-navbar.component.html',
  styleUrl: './top-navbar.component.scss',
  standalone: true
})
export class TopNavbarComponent implements OnInit {
  private restaurantStatusService = inject(RestaurantStatusService);
  private publicScheduleStore = inject(PublicScheduleStore);
  private cartConfigStore = inject(CartConfigStore);

  // Signaux partagés uniques issus des Stores
  schedule = this.publicScheduleStore.schedule;
  cartConfig = this.cartConfigStore.cartConfig;
  isCartConfigLoaded = this.cartConfigStore.isLoaded;

  ngOnInit(): void {
    this.cartConfigStore.loadConfig();
  }

  restaurantStatus = computed(() => {
    const data = this.schedule();
    if (!data) return null;
    return this.restaurantStatusService.getCurrentStatus(data);
  });

  private parseLocalDate(dateStr?: string): Date | null {
    if (!dateStr) return null;
    const cleanStr = dateStr.split('.')[0].replace('Z', '');
    const parts = cleanStr.split('T');
    if (parts.length !== 2) return new Date(dateStr);

    const [year, month, day] = parts[0].split('-').map(Number);
    const [hours, minutes, seconds] = parts[1].split(':').map(Number);

    return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
  }

  private formatTimeDirect(dateStr?: string): string {
    if (!dateStr) return '';
    const timePart = dateStr.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  }

  private formatDateDirect(dateStr?: string): string {
    if (!dateStr) return '';
    const datePart = dateStr.split('T')[0];
    if (!datePart) return '';
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  }

  isCartDisabled = computed(() => {
    const cart = this.cartConfig();
    if (!cart) return false;
    if (cart.active) return false;

    const now = new Date().getTime();
    const start = this.parseLocalDate(cart.startDate)?.getTime() ?? null;
    const end = this.parseLocalDate(cart.endDate)?.getTime() ?? null;

    if (start && end) return now >= start && now <= end;
    if (start) return now >= start;
    if (end) return now <= end;

    return true;
  });

  getDisabledPeriodMessage = computed<string>(() => {
    const cart = this.cartConfig();
    const contactSuffix = " Merci de contacter l'administration.";

    if (!cart) return 'Les commandes en ligne sont actuellement désactivées.';

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const startRaw = cart.startDate?.split('T')[0];
    const endRaw = cart.endDate?.split('T')[0];

    const startTimeStr = this.formatTimeDirect(cart.startDate);
    const endTimeStr = this.formatTimeDirect(cart.endDate);

    const startDateFormatted = this.formatDateDirect(cart.startDate);
    const endDateFormatted = this.formatDateDirect(cart.endDate);

    if (startRaw && endRaw) {
      const startIsToday = startRaw === todayStr;
      const endIsToday = endRaw === todayStr;

      if (startIsToday && endIsToday) {
        return `🛒 Les commandes en ligne sont désactivées de ${startTimeStr} à ${endTimeStr}.` + contactSuffix;
      }

      if (startIsToday) {
        return `🛒 Les commandes en ligne sont désactivées à partir de ${startTimeStr} jusqu'au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
      }

      return `🛒 Les commandes en ligne sont désactivées du ${startDateFormatted} à ${startTimeStr} au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
    }

    if (startRaw) {
      if (startRaw === todayStr) {
        return `🛒 Les commandes en ligne sont désactivées à partir de ${startTimeStr}.` + contactSuffix;
      }
      return `🛒 Les commandes en ligne sont désactivées à partir du ${startDateFormatted} à ${startTimeStr}.` + contactSuffix;
    }

    if (endRaw) {
      if (endRaw === todayStr) {
        return `🛒 Les commandes en ligne sont désactivées jusqu'à ${endTimeStr}.` + contactSuffix;
      }
      return `🛒 Les commandes en ligne sont désactivées jusqu'au ${endDateFormatted} à ${endTimeStr}.` + contactSuffix;
    }

    return '🛒 Les commandes en ligne sont temporairement désactivées.';
  });

  bannerMessage = computed<BannerMessage>(() => {
    if (!this.schedule() || !this.isCartConfigLoaded()) {
      return null;
    }

    // Priorité 1 : Si le panier est désactivé
    if (this.isCartDisabled()) {
      return {
        type: 'cart-disabled',
        text: this.getDisabledPeriodMessage()
      };
    }

    // Priorité 2 : Si le restaurant est fermé selon le calendrier
    const status = this.restaurantStatus();
    if (status && !status.isOpen) {
      return {
        type: 'closed',
        text: status.nextOpening || 'Restaurant fermé'
      };
    }

    return null;
  });

  shouldDisplay = computed(() => {
    if (!this.schedule() || !this.isCartConfigLoaded()) {
      return false;
    }
    return this.bannerMessage() !== null;
  });
}
